/**
 * Özet (v0.1.175; rebuilt as the summary dashboard in v0.1.257, specs/ozet-pano.md, the design Sercan approved on
 * 2 Nov 2026). On top what waits for him (the oldest open Sekreter items), then a period switch (Bugün, Bu hafta, Bu
 * ay, Son 30 gün) that reads GET /ozet/summary again, how fresh each source is, eight headline tiles against the
 * previous period, two daily charts (visits by source, Etsy revenue), one card per channel, the last eight weeks in a
 * table (the running week marked) and what is not measured, said in words instead of a zero. v0.1.266: eleven tiles
 * (Etsy orders, Etsy clicks and Pinterest impressions added) and six line charts (ozet-lines.tsx) under the two. Counts only: no customer
 * name, address or message text reaches this page. The page reads both again every minute while it is open.
 */

import { SEO_ENGINE_LABELS, SITE_SOURCE_GROUP_LABELS, SITE_SOURCE_GROUPS } from '@twin/contracts';
import { type ReactNode, useEffect } from 'react';
import type {
  OzetNotMeasured,
  OzetPeriod,
  OzetSummaryDay,
  OzetSummarySource,
  OzetSummaryTotals,
  OzetSummaryView,
  OzetView,
} from '../api/ozet.js';
import { usePanel } from '../app/context.js';
import { useIsDesktop } from '../app/media.js';
import { useOzet } from '../app/ozet.js';
import { Link } from '../app/router.js';
import { formatIsoDay, formatNumber, formatRelativeClock, formatUsd, formatUsdPair } from '../lib/format.js';
import { Segmented } from '../screens/common.js';
import { Icon } from './icons.js';
import { centsText, OzetSalesChart, OzetVisitsChart } from './ozet-charts.js';
import {
  OzetChannelLineChart,
  OzetDailyVisitsChart,
  OzetMailLinesChart,
  OzetPeriodSalesChart,
  OzetSourceLinesChart,
} from './ozet-lines.js';
import { type Delta, deltaOf, KpiTile } from './pulse.js';

const PERIOD_OPTIONS: readonly { id: OzetPeriod; label: string; count: null }[] = [
  { id: 'day', label: 'Bugün', count: null },
  { id: 'week', label: 'Bu hafta', count: null },
  { id: 'month', label: 'Bu ay', count: null },
  { id: 'd30', label: 'Son 30 gün', count: null },
];

const PERIOD_NAMES: Readonly<Record<OzetPeriod, string>> = Object.freeze({
  day: 'Bugün',
  week: 'Bu hafta',
  month: 'Bu ay',
  d30: 'Son 30 gün',
});

/** What the previous period is called on the page. */
const PREVIOUS_NAMES: Readonly<Record<OzetPeriod, string>> = Object.freeze({
  day: 'dün',
  week: 'geçen haftanın aynı günleri',
  month: 'geçen ayın aynı günleri',
  d30: 'önceki 30 gün',
});

/** The previous period with "in" (bulunma hâli), for the line charts' takeaway: "geçen haftanın aynı günlerinde 100 $". */
const PREVIOUS_IN: Readonly<Record<OzetPeriod, string>> = Object.freeze({
  day: 'dün',
  week: 'geçen haftanın aynı günlerinde',
  month: 'geçen ayın aynı günlerinde',
  d30: 'önceki 30 günde',
});

const GM_REASON_LABELS: Readonly<Record<string, string>> = Object.freeze({
  rate_limit: 'abonelik kullanım sınırı doldu',
  config: 'ayarlarda kapalı',
  manual: 'elle durduruldu',
  budget: 'bütçe doldu',
});

/** Why a number is missing, said where the number would be. */
export const NOT_MEASURED_TEXT: Readonly<Record<OzetNotMeasured, string>> = Object.freeze({
  outreach_opens: 'Tanıtım maillerinin açılması: maillerde izleme pikseli yok, açılma ölçülmüyor.',
  site_orders:
    'Site siparişi: sitede ödeme yok, satın al bağlantıları Etsy’ye gider (siteden Etsy’ye tıklama sayılıyor).',
  etsy_daily_visits: 'Etsy mağaza ziyareti, gün gün: Etsy bu sayıyı yalnız aylık veriyor.',
  cgtrader_sales: 'CGTrader satış ve kazanç: henüz okunmuyor.',
});

/** What cannot be judged yet even where it is counted; shown with the unmeasured items. */
export const OZET_NOT_YET: readonly string[] = Object.freeze([
  'Cevap oranı: anlamlı olması için en az 100 teslim edilmiş tanıtım maili gerekir.',
  'Siteden Etsy satışına geçiş: Etsy siparişin nereden geldiğini bildirmiyor.',
  '5 Ekim öncesi site ziyaretleri: eski sitenin sunucu kaydı yok.',
  "Twin'in satışa etkisi: ayda 7 civarı siparişle aylarca ayırt edilemez.",
  'Yapay zekâ asistanlarında görünme eğilimi: cevaplar haftadan haftaya oynuyor; en az iki hafta süren değişim sayılır.',
]);

const SOURCE_LABELS: Readonly<Record<OzetSummarySource['id'], string>> = Object.freeze({
  etsy: 'Etsy',
  server_log: 'Site kaydı',
  ga4: 'Site ziyaretçi sayımı',
  search_console: 'Google arama',
  bing: 'Bing',
  youtube: 'YouTube',
  pinterest: 'Pinterest',
  cgtrader: 'CGTrader',
});

function n(value: number): string {
  return formatNumber(value);
}

function micro(value: number): string {
  return formatUsd(value, 'auto');
}

/** A percent for big numbers, a plain difference for small ones (a percent of 1 against 3 says more than it knows). */
function delta(now: number | null, before: number | null): Delta | null {
  if (now === null || before === null) return null;
  return deltaOf(now, before, Math.max(now, before) < 20 ? 'count' : 'pct');
}

function rangeText(view: OzetSummaryView): string {
  const { current } = view;
  const days =
    current.from === current.to
      ? formatIsoDay(current.from)
      : `${formatIsoDay(current.from)} - ${formatIsoDay(current.to)}`;
  return `${PERIOD_NAMES[view.period]} (${days}) · karşılaştırma: ${PREVIOUS_NAMES[view.period]}`;
}

function Decisions({ view }: { view: OzetView }) {
  const { open, items } = view.decisions;
  const more = open - items.length;
  return (
    <section className="au-sum oz-decisions" aria-labelledby="oz-dec-h">
      <h2 id="oz-dec-h" className="au-sum__title">
        Senden bekleyen: <span className="mono accent">{n(open)}</span> madde
      </h2>
      {items.length === 0 ? (
        <p className="oz-muted">Bekleyen madde yok.</p>
      ) : (
        <ol className="oz-dec-list">
          {items.map((item) => (
            <li key={item.code} className="oz-dec">
              <span className="oz-dec__title">{item.titleTr}</span>
              <span className="oz-dec__meta">
                {item.ageDays === 0 ? 'bugün eklendi' : `${n(item.ageDays)} gündür bekliyor`}
                {item.placeTr === null || item.placeTr === '' ? '' : ` · ${item.placeTr}`}
              </span>
            </li>
          ))}
        </ol>
      )}
      <Link to="/sekreter" className="oz-link">
        {more > 0 ? `${n(more)} madde daha · Sekreter` : 'Sekreter'}
        <Icon name="chevron-right" size={14} />
      </Link>
    </section>
  );
}

function sourceText(source: OzetSummarySource, now: number): string {
  if (source.state === 'not_connected') return 'bağlı değil';
  if (source.state === 'waiting') return 'ilk okuma bekleniyor';
  if (source.state === 'error') return 'son okuma başarısız';
  if (source.state === 'no_data') return 'veri yok';
  if (source.id === 'search_console' || source.id === 'bing' || source.id === 'ga4') {
    const through = source.dataThrough === null ? 'henüz gün yok' : `son gün ${formatIsoDay(source.dataThrough)}`;
    return source.id === 'search_console' ? `${through} (2-3 gün geriden gelir)` : through;
  }
  return source.readAt === null ? 'henüz okunmadı' : formatRelativeClock(Date.parse(source.readAt), now);
}

function chipTone(source: OzetSummarySource, today: string): 'ok' | 'late' | 'off' {
  if (source.state === 'not_connected' || source.state === 'waiting' || source.state === 'no_data') return 'off';
  if (source.state === 'error') return 'late';
  if (
    (source.id === 'search_console' || source.id === 'bing') &&
    (source.dataThrough === null || source.dataThrough < today)
  ) {
    return 'late';
  }
  return 'ok';
}

function Freshness({ view, now }: { view: OzetSummaryView; now: number }) {
  return (
    <ul className="oz-fresh" aria-label="Verinin güncelliği">
      {view.sources.map((source) => (
        <li key={source.id} className={`oz-chip oz-chip--${chipTone(source, view.today)}`}>
          <span className="oz-chip__dot" aria-hidden="true" />
          {SOURCE_LABELS[source.id]}: {sourceText(source, now)}
        </li>
      ))}
      <li className="oz-chip oz-chip--off">
        <span className="oz-chip__dot" aria-hidden="true" />
        Etsy ziyaret: yalnız aylık
      </li>
    </ul>
  );
}

/** The daily values of a series that has them (null days left out: the sparkline is decoration). */
function series(days: readonly OzetSummaryDay[], pick: (day: OzetSummaryDay) => number | null): number[] {
  return days.map(pick).filter((value): value is number => value !== null);
}

/**
 * The headline tiles against the previous period (v0.1.266: the eight Sercan asked for first, Etsy orders, Etsy
 * clicks and Pinterest impressions added; then AI arrivals, Google clicks and the replies).
 */
function Tiles({ view }: { view: OzetSummaryView }) {
  const now = view.totals.current;
  const before = view.totals.previous;
  const previous = PREVIOUS_NAMES[view.period];
  const base = (value: number | null, text: (v: number) => string = n) =>
    `${previous} ${value === null ? '-' : text(value)}`;
  const days = view.daily;
  const social = view.social.youtube;
  const pinterest = view.social.pinterest;
  return (
    <section className="oz-kpis" aria-label="Dönem özeti">
      <KpiTile
        label="Etsy geliri"
        value={centsText(now.sales.revenueCents)}
        delta={delta(now.sales.revenueCents, before.sales.revenueCents)}
        base={base(before.sales.revenueCents, centsText)}
        spark={series(days, (day) => day.revenueCents)}
        source={`${n(now.sales.orders)} sipariş · iptal ve iade hariç`}
        to="/satislar"
        page="Satışlar"
      />
      <KpiTile
        label="Etsy siparişi"
        value={n(now.sales.orders)}
        delta={delta(now.sales.orders, before.sales.orders)}
        base={base(before.sales.orders)}
        spark={series(days, (day) => day.orders)}
        source="iptal ve iade hariç"
        to="/satislar"
        page="Satışlar"
      />
      <KpiTile
        label="Site oturumu"
        value={now.site.sessions === null ? '-' : n(now.site.sessions)}
        delta={delta(now.site.sessions, before.site.sessions)}
        base={base(before.site.sessions)}
        spark={series(days, (day) => day.sessions)}
        source={now.site.sessions === null ? 'sayım ertesi sabah gelir' : `${n(now.site.pageViews ?? 0)} sayfa açıldı`}
        to="/site"
        page="Site"
      />
      <KpiTile
        label="Etsy’ye tıklama"
        value={now.site.etsyClicks === null ? '-' : n(now.site.etsyClicks)}
        delta={delta(now.site.etsyClicks, before.site.etsyClicks)}
        base={base(before.site.etsyClicks)}
        spark={series(days, (day) => day.etsyClicks)}
        source="sitedeki Etsy linklerinden"
        to="/site"
        page="Site"
      />
      <KpiTile
        label="Bize gelen mesaj"
        value={n(now.inbox.messagesIn)}
        delta={delta(now.inbox.messagesIn, before.inbox.messagesIn)}
        base={base(before.inbox.messagesIn)}
        spark={series(days, (day) => day.messagesIn)}
        source={`mail, site formu, Etsy · ${n(now.inbox.siteForm)} yeni form`}
        to="/musteriler"
        page="Müşteriler"
      />
      <KpiTile
        label="Gönderilen tanıtım maili"
        value={n(now.outreach.sent)}
        delta={delta(now.outreach.sent, before.outreach.sent)}
        base={base(before.outreach.sent)}
        spark={series(days, (day) => day.outreachSent)}
        source={`${n(now.outreach.replies)} cevap · açılma ölçülmüyor`}
        to="/tanitim-takibi"
        page="Tanıtım takibi"
      />
      <KpiTile
        label="YouTube izlenme"
        value={now.youtubeViews === null ? '-' : n(now.youtubeViews)}
        delta={delta(now.youtubeViews, before.youtubeViews)}
        base={base(before.youtubeViews)}
        spark={series(days, (day) => day.youtubeViews)}
        source={
          social === null
            ? 'YouTube henüz okunmadı'
            : `toplam ${social.views === null ? '-' : n(social.views)} · ${social.subscribers === null ? '-' : n(social.subscribers)} abone`
        }
        to="/kanallar"
        page="Kanallar"
      />
      <KpiTile
        label="Pinterest gösterim"
        value={now.pinterestImpressions === null ? '-' : n(now.pinterestImpressions)}
        delta={delta(now.pinterestImpressions, before.pinterestImpressions)}
        base={base(before.pinterestImpressions)}
        spark={series(days, (day) => day.pinterestImpressions)}
        source={
          now.pinterestImpressions !== null
            ? 'Pinterest bir gün geriden verir'
            : pinterest === null
              ? 'Pinterest henüz okunmadı'
              : 'bu günler için günlük sayı yok'
        }
        to="/kanallar"
        page="Kanallar"
      />
      <KpiTile
        label="Yapay zekâdan gelen"
        value={now.site.aiEntries === null ? '-' : n(now.site.aiEntries)}
        delta={delta(now.site.aiEntries, before.site.aiEntries)}
        base={base(before.site.aiEntries)}
        spark={series(days, (day) => day.aiEntries)}
        source="asistanın önerdiği linkten"
        to="/site"
        page="Site"
      />
      <KpiTile
        label="Google tıklama"
        value={now.google === null ? '-' : n(now.google.clicks)}
        delta={delta(now.google?.clicks ?? null, before.google?.clicks ?? null)}
        base={base(before.google?.clicks ?? null)}
        spark={series(days, (day) => day.googleClicks)}
        source={now.google === null ? 'Google verisi 2-3 gün geriden gelir' : `${n(now.google.impressions)} gösterim`}
        to="/seo"
        page="SEO ve yapay zekâ"
      />
      <KpiTile
        label="Müşterilere cevap"
        value={n(now.inbox.repliesOut)}
        delta={delta(now.inbox.repliesOut, before.inbox.repliesOut)}
        base={base(before.inbox.repliesOut)}
        spark={series(days, (day) => day.repliesOut)}
        source="Twin'in ve senin gönderdiğin"
        to="/musteriler"
        page="Müşteriler"
      />
    </section>
  );
}

interface Row {
  label: string;
  /** null: the reason in `missing` is said instead of a number. */
  now: number | null;
  before?: number | null;
  text?: (value: number) => string;
  missing?: string;
}

function Rows({ rows }: { rows: readonly Row[] }) {
  return (
    <dl className="oz-rows">
      {rows.map((row) => {
        const change = row.before === undefined ? null : delta(row.now, row.before);
        return (
          <div key={row.label} className="oz-row">
            <dt>{row.label}</dt>
            <dd className={row.now === null ? 'oz-row__na' : 'mono'}>
              {row.now === null ? (row.missing ?? 'ölçülmüyor') : (row.text ?? n)(row.now)}
            </dd>
            <dd className={change === null ? 'oz-row__delta' : `oz-row__delta kpi-tile__delta--${change.tone}`}>
              {change === null ? '' : change.text}
              {change === null ? null : <span className="sr-only"> ({change.spoken})</span>}
            </dd>
          </div>
        );
      })}
    </dl>
  );
}

function ChannelCard({
  id,
  title,
  tag,
  warn = false,
  children,
}: {
  id: string;
  title: string;
  tag: string;
  warn?: boolean;
  children: ReactNode;
}) {
  const headingId = `oz-${id}-h`;
  return (
    <article className="au-card oz-card" aria-labelledby={headingId}>
      <header className="oz-card__head">
        <h3 id={headingId} className="au-card__name">
          {title}
        </h3>
        <span className={warn ? 'oz-tag oz-tag--warn' : 'oz-tag'}>{tag}</span>
      </header>
      {children}
    </article>
  );
}

function lastVisibilityWeek(view: OzetSummaryView) {
  const last = view.aiVisibility.at(-1)?.weekStart;
  return last === undefined ? [] : view.aiVisibility.filter((week) => week.weekStart === last);
}

function Cards({ view, ozet }: { view: OzetSummaryView; ozet: OzetView | null }) {
  const now: OzetSummaryTotals = view.totals.current;
  const before: OzetSummaryTotals = view.totals.previous;
  const sources = SITE_SOURCE_GROUPS.filter(
    (group) => (now.site.sourceSessions[group] ?? 0) > 0 || (before.site.sourceSessions[group] ?? 0) > 0,
  );
  const visibility = lastVisibilityWeek(view);
  const youtube = view.social.youtube;
  const pinterest = view.social.pinterest;
  const team = ozet?.team ?? null;
  const gmPaused = team?.gmStatus === 'paused';
  return (
    <section className="oz-cards" aria-label="Kanallar">
      <ChannelCard id="sales" title="Satış" tag="Etsy">
        <Rows
          rows={[
            { label: 'Gelir', now: now.sales.revenueCents, before: before.sales.revenueCents, text: centsText },
            { label: 'Sipariş', now: now.sales.orders, before: before.sales.orders },
            { label: 'Kesintiler sonrası kalan', now: now.sales.contributionCents, text: (v) => centsText(v) },
            { label: 'Etsy kesintisi (tahmin)', now: now.sales.feesCents, text: centsText },
            {
              label: 'Siteden Etsy’ye tıklama',
              now: now.site.etsyClicks,
              before: before.site.etsyClicks,
              missing: 'site kaydı yok',
            },
            { label: 'Etsy mağaza ziyareti', now: null, missing: 'Etsy yalnız aylık veriyor' },
          ]}
        />
      </ChannelCard>
      <ChannelCard id="site" title="Site ve kaynaklar" tag="cadgaragepro.com">
        <Rows
          rows={[
            {
              label: 'Ziyaretçi',
              now: now.site.sessions,
              before: before.site.sessions,
              missing: 'sayım ertesi sabah gelir',
            },
            ...sources.map((group) => ({
              label: SITE_SOURCE_GROUP_LABELS[group],
              now: now.site.sessions === null ? null : (now.site.sourceSessions[group] ?? 0),
              before: before.site.sessions === null ? null : (before.site.sourceSessions[group] ?? 0),
              missing: 'sayım ertesi sabah gelir',
            })),
            {
              label: 'Dışarıdan giriş (sunucu kaydı)',
              now: now.site.entries,
              before: before.site.entries,
              missing: 'kayıt yok',
            },
            { label: 'Açılan sayfa', now: now.site.pageViews, before: before.site.pageViews, missing: 'kayıt yok' },
            {
              label: 'Tanıtım mailinden gelen',
              now: now.site.outreachEntries,
              before: before.site.outreachEntries,
              missing: 'kayıt yok',
            },
          ]}
        />
      </ChannelCard>
      <ChannelCard id="search" title="Arama motorları" tag="2-3 gün gecikmeli" warn>
        <Rows
          rows={[
            {
              label: 'Google gösterim',
              now: now.google?.impressions ?? null,
              before: before.google?.impressions ?? null,
              missing: 'henüz gelmedi',
            },
            {
              label: 'Google tıklama',
              now: now.google?.clicks ?? null,
              before: before.google?.clicks ?? null,
              missing: 'henüz gelmedi',
            },
            {
              label: 'Google ortalama sıra',
              now: now.google?.position ?? null,
              text: (v) => formatNumber(v, 1),
              missing: 'henüz gelmedi',
            },
            {
              label: 'Bing gösterim',
              now: now.bing?.impressions ?? null,
              before: before.bing?.impressions ?? null,
              missing: 'henüz gelmedi',
            },
            {
              label: 'Bing tıklama',
              now: now.bing?.clicks ?? null,
              before: before.bing?.clicks ?? null,
              missing: 'henüz gelmedi',
            },
          ]}
        />
      </ChannelCard>
      <ChannelCard id="ai" title="Yapay zekâ görünürlüğü" tag="haftalık kontrol">
        <Rows
          rows={[
            {
              label: 'Asistandan siteye gelen',
              now: now.site.aiEntries,
              before: before.site.aiEntries,
              missing: 'kayıt yok',
            },
            {
              label: 'Asistanın canlı sayfa açması',
              now: now.site.aiLiveFetches,
              before: before.site.aiLiveFetches,
              missing: 'kayıt yok',
            },
            {
              label: 'Asistanların dizin okuması',
              now: now.site.aiIndexFetches,
              before: before.site.aiIndexFetches,
              missing: 'kayıt yok',
            },
            ...(visibility.length === 0
              ? [{ label: 'Haftalık soru kontrolü', now: null, missing: 'henüz yapılmadı' }]
              : visibility.map((week) => ({
                  label: `${SEO_ENGINE_LABELS[week.engine]} (${formatIsoDay(week.weekStart)} haftası)`,
                  now: week.found,
                  text: (v: number) => `${n(v)} / ${n(week.asked)} soruda`,
                }))),
          ]}
        />
      </ChannelCard>
      <ChannelCard id="inbox" title="Müşteri iletişimi" tag="Gmail · site · Etsy">
        <Rows
          rows={[
            { label: 'Yeni konuşma: Gmail', now: now.inbox.gmail, before: before.inbox.gmail },
            { label: 'Yeni konuşma: site formu', now: now.inbox.siteForm, before: before.inbox.siteForm },
            { label: 'Yeni konuşma: Etsy', now: now.inbox.etsy, before: before.inbox.etsy },
            { label: 'Gelen mesaj', now: now.inbox.messagesIn, before: before.inbox.messagesIn },
            { label: 'Giden cevap', now: now.inbox.repliesOut, before: before.inbox.repliesOut },
            {
              label: 'Site asistanı sohbeti',
              now: now.siteChat?.chats ?? null,
              before: before.siteChat?.chats ?? null,
              missing: '90 günden eskisi tutulmuyor',
            },
          ]}
        />
      </ChannelCard>
      <ChannelCard id="outreach" title="Tanıtım mailleri" tag="Avustralya, Yeni Zelanda">
        <Rows
          rows={[
            { label: 'Gönderilen', now: now.outreach.sent, before: before.outreach.sent },
            { label: 'Geri dönen', now: now.outreach.bounced, before: before.outreach.bounced },
            { label: 'Cevap', now: now.outreach.replies, before: before.outreach.replies },
            { label: 'İlgilenen', now: now.outreach.interested, before: before.outreach.interested },
            { label: 'Linke tıklayan firma', now: now.outreach.clickFirms, before: before.outreach.clickFirms },
            { label: 'Açılma', now: null, missing: 'ölçülmüyor (izleme pikseli yok)' },
          ]}
        />
      </ChannelCard>
      <ChannelCard id="social" title="Sosyal medya" tag="günlük okuma">
        <Rows
          rows={[
            {
              label: 'YouTube izlenme (dönemde)',
              now: now.youtubeViews,
              before: before.youtubeViews,
              missing: 'okuma yok',
            },
            { label: 'YouTube izlenme (toplam)', now: youtube?.views ?? null, missing: 'henüz okunmadı' },
            { label: 'YouTube abone', now: youtube?.subscribers ?? null, missing: 'henüz okunmadı' },
            {
              label: 'Pinterest gösterim (dönemde)',
              now: now.pinterestImpressions,
              before: before.pinterestImpressions,
              missing: 'günlük sayı yok',
            },
            { label: 'Pinterest gösterim (30 gün)', now: pinterest?.impressions30d ?? null, missing: 'henüz okunmadı' },
            {
              label: 'Pinterest siteye tıklama (30 gün)',
              now: pinterest?.outboundClicks30d ?? null,
              missing: 'henüz okunmadı',
            },
            { label: 'CGTrader yayındaki model', now: view.social.cgtrader?.models ?? null, missing: 'henüz okunmadı' },
            { label: 'CGTrader satış', now: null, missing: 'henüz okunmuyor' },
          ]}
        />
      </ChannelCard>
      <ChannelCard id="team" title="Ekip ve maliyet" tag="Twin" warn={gmPaused}>
        <Rows
          rows={[
            { label: 'Biten iş', now: now.team.tasksDone, before: before.team.tasksDone },
            { label: 'Verilen karar', now: now.team.approvalsDecided, before: before.team.approvalsDecided },
            { label: 'Onaylanan', now: now.team.approvalsApproved, before: before.team.approvalsApproved },
            { label: 'Reddedilen', now: now.team.approvalsRejected, before: before.team.approvalsRejected },
            { label: 'Harcama (dönemde)', now: now.team.costMicroUsd, before: before.team.costMicroUsd, text: micro },
          ]}
        />
        {team === null ? null : (
          <p className={gmPaused ? 'oz-line oz-line--warn' : 'oz-line'}>
            Genesis bu ay:{' '}
            <span className="mono">{formatUsdPair(team.genesisSpentMicroUsd, team.genesisCapMicroUsd)}</span>.{' '}
            {gmPaused
              ? `Genel Müdür duraklatıldı: ${GM_REASON_LABELS[team.gmStatusReason ?? ''] ?? 'sebep bilinmiyor'}.`
              : team.gmStatus === 'active' || team.gmStatus === 'trial'
                ? 'Genel Müdür çalışıyor.'
                : 'Genel Müdür kapalı.'}
          </p>
        )}
      </ChannelCard>
    </section>
  );
}

function cell(value: number | null, text: (v: number) => string = n): string {
  return value === null ? '-' : text(value);
}

function Weeks({ view }: { view: OzetSummaryView }) {
  return (
    <section className="au-card oz-weeks" aria-labelledby="oz-weeks-h">
      <header className="oz-card__head">
        <h2 id="oz-weeks-h" className="au-card__name">
          Hafta hafta
        </h2>
        <span className="oz-tag">Pazartesi başlar · bu hafta sürüyor</span>
      </header>
      <div className="sx-scroll">
        <table className="sx-table oz-table">
          <thead>
            <tr>
              <th scope="col">Hafta</th>
              <th scope="col">Ziyaretçi</th>
              <th scope="col">Dışarıdan giriş</th>
              <th scope="col">Yapay zekâdan</th>
              <th scope="col">Google tık.</th>
              <th scope="col">Google gösterim</th>
              <th scope="col">Sipariş</th>
              <th scope="col">Gelir</th>
              <th scope="col">Gelen mesaj</th>
              <th scope="col">Cevap</th>
              <th scope="col">Tanıtım maili</th>
            </tr>
          </thead>
          <tbody>
            {view.weekly.map((week) => (
              <tr key={week.weekStart} className={week.running ? 'oz-running' : undefined}>
                <th scope="row">
                  {week.running
                    ? `${formatIsoDay(week.weekStart)} (sürüyor)`
                    : `${formatIsoDay(week.weekStart)} - ${formatIsoDay(week.to)}`}
                </th>
                <td className="mono">{cell(week.sessions)}</td>
                <td className="mono">{cell(week.entries)}</td>
                <td className="mono">{cell(week.aiEntries)}</td>
                <td className="mono">{cell(week.googleClicks)}</td>
                <td className="mono">{cell(week.googleImpressions)}</td>
                <td className="mono">{n(week.orders)}</td>
                <td className="mono">{centsText(week.revenueCents)}</td>
                <td className="mono">{n(week.messagesIn)}</td>
                <td className="mono">{n(week.repliesOut)}</td>
                <td className="mono">{n(week.outreachSent)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function NotMeasured({ view }: { view: OzetSummaryView | null }) {
  const keys = view?.notMeasured ?? [];
  return (
    <section className="oz-notyet" aria-labelledby="oz-notyet-h">
      <h2 id="oz-notyet-h" className="sx-section__title">
        Henüz ölçülemeyenler
      </h2>
      <ul>
        {keys.map((key) => (
          <li key={key}>{NOT_MEASURED_TEXT[key]}</li>
        ))}
        {OZET_NOT_YET.map((line) => (
          <li key={line}>{line}</li>
        ))}
      </ul>
    </section>
  );
}

function ChartCard({ id, title, children }: { id: string; title: string; children: ReactNode }) {
  return (
    <section className="au-card oz-chart-card" aria-labelledby={`${id}-h`}>
      <h2 id={`${id}-h`} className="au-card__name">
        {title}
      </h2>
      {children}
    </section>
  );
}

function Summary({ view, ozet, desktop }: { view: OzetSummaryView; ozet: OzetView | null; desktop: boolean }) {
  const now = usePanel().now;
  return (
    <>
      <Freshness view={view} now={now} />
      <Tiles view={view} />
      <div className={desktop ? 'oz-two oz-two--desk' : 'oz-two'}>
        <section className="au-card oz-chart-card" aria-labelledby="oz-visitschart-h">
          <h2 id="oz-visitschart-h" className="au-card__name">
            Siteye kim nereden geldi
          </h2>
          <OzetVisitsChart daily={view.daily} current={view.current} />
        </section>
        <section className="au-card oz-chart-card" aria-labelledby="oz-saleschart-h">
          <h2 id="oz-saleschart-h" className="au-card__name">
            Satış, gün gün
          </h2>
          <OzetSalesChart daily={view.daily} current={view.current} />
        </section>
      </div>
      <div className={desktop ? 'oz-two oz-two--desk' : 'oz-two'}>
        <ChartCard id="oz-periodsales" title="Bu dönem ve önceki dönem">
          <OzetPeriodSalesChart
            daily={view.daily}
            current={view.current}
            previous={view.previous}
            previousName={PREVIOUS_NAMES[view.period]}
            previousIn={PREVIOUS_IN[view.period]}
          />
        </ChartCard>
        <ChartCard id="oz-dailyvisits" title="Günlük ziyaret">
          <OzetDailyVisitsChart
            daily={view.daily}
            current={view.current}
            previous={view.previous}
            previousName={PREVIOUS_NAMES[view.period]}
            previousIn={PREVIOUS_IN[view.period]}
          />
        </ChartCard>
        <ChartCard id="oz-sourcelines" title="Kanallara göre günlük ziyaret">
          <OzetSourceLinesChart daily={view.daily} current={view.current} />
        </ChartCard>
        <ChartCard id="oz-maillines" title="Gün gün mailler">
          <OzetMailLinesChart daily={view.daily} current={view.current} />
        </ChartCard>
        <ChartCard id="oz-youtubeline" title="YouTube günlük izlenme">
          <OzetChannelLineChart daily={view.daily} current={view.current} channel="youtube" />
        </ChartCard>
        <ChartCard id="oz-pinterestline" title="Pinterest günlük gösterim">
          <OzetChannelLineChart daily={view.daily} current={view.current} channel="pinterest" />
        </ChartCard>
      </div>
      <Cards view={view} ozet={ozet} />
      <Weeks view={view} />
    </>
  );
}

export function OzetPage() {
  const { store, state } = useOzet();
  const desktop = useIsDesktop();
  const now = usePanel().now;
  useEffect(() => store.watch(), [store]);
  const summary = state.summaries[state.period] ?? null;
  const loading = state.loading || state.summaryLoading;
  const firstError = state.view === null && summary === null ? (state.error ?? state.summaryError) : null;
  if (state.view === null && summary === null) {
    return firstError === null ? (
      <div className="loading" role="status">
        Yükleniyor…
      </div>
    ) : (
      <div className="shop-status" role="status">
        Özet yüklenemedi: {firstError}{' '}
        <button type="button" className="btn btn--secondary" onClick={() => void store.refresh()}>
          Tekrar dene
        </button>
      </div>
    );
  }
  const generatedAt = summary?.generatedAt ?? state.view?.generatedAt ?? null;
  const staleError = state.error ?? state.summaryError;
  return (
    <div className={desktop ? 'au au--desk oz' : 'au oz'}>
      <div className="oz-toolbar">
        <p className="oz-muted">
          {generatedAt === null ? '' : `${formatRelativeClock(Date.parse(generatedAt), now)} itibarıyla`} · sayfa
          açıkken dakikada bir yenilenir
        </p>
        <button type="button" className="btn btn--secondary" disabled={loading} onClick={() => void store.refresh()}>
          {loading ? 'Yenileniyor…' : 'Yenile'}
        </button>
      </div>
      {staleError === null ? null : (
        <p className="shop-status" role="status">
          Yenilenemedi: {staleError}
        </p>
      )}
      {state.view === null ? null : <Decisions view={state.view} />}
      <div className="oz-period">
        <p className="oz-range">{summary === null ? PERIOD_NAMES[state.period] : rangeText(summary)}</p>
        <Segmented
          label="Dönem"
          options={PERIOD_OPTIONS}
          value={state.period}
          onChange={(period) => void store.setPeriod(period)}
          controls="oz-period-panel"
        />
      </div>
      <div
        id="oz-period-panel"
        role="tabpanel"
        aria-labelledby={`tab-oz-period-panel-${state.period}`}
        className="oz-panel"
      >
        {summary === null ? (
          state.summaryError === null ? (
            <div className="loading" role="status">
              Yükleniyor…
            </div>
          ) : null
        ) : (
          <Summary view={summary} ozet={state.view} desktop={desktop} />
        )}
      </div>
      <NotMeasured view={summary} />
    </div>
  );
}
